import i18next from 'i18next'
import type { UserProfile } from '../../../../shared/domain/users/types.no-deps'
import { BaseBottomSheet } from '../../ui/BaseBottomSheet'
import { FeedbackSubmission } from './FeedbackSubmission'
import type { CommonDeps } from '../../base/CommonDeps'
import type { UUID } from '../../../../shared/lib/typeHelpers.no-deps'
import type { CamelCaseFlags } from '../../../../shared/cfg/flags.no-deps'
import { CommentSection } from '../../commentTimeline/CommentSection'
import { type BottomSheetOptionProps, BottomSheetOptions } from '../../ui/BottomSheetOptions'
import React from 'react'
import type { LoopPath } from '../../../../shared/domain/flows/LoopPath.no-deps'
import type { RichTextComment } from '../../../../shared/domain/postComments/types.no-deps'
import type { ElementType } from '../../../../shared/domain/flows/types.no-deps'
import { elementDisplayName } from '../../../../shared/domain/flows/element'

export type QuotedElement = {
    id: UUID
    label: string
    type: ElementType
    loopPath: LoopPath
}

export type ElementSheet =
    | {
          mode: 'options'
          element: QuotedElement
      }
    | {
          mode: 'feedback'
          element: QuotedElement
      }
    | {
          mode: 'comments'
          element: QuotedElement | null
          focusedComment?: UUID
      }

type Props = {
    onClose: () => void
    onFeedbackSubmission: (message: string) => void
    onCommentsClick: () => void
    onFeedbackClick: () => void
    onClearElement: () => void
    onCommentClick?: (comment: RichTextComment) => void
    onQuoteClick?: (comment: RichTextComment) => void
    isQuoteClickable?: (comment: RichTextComment) => boolean
    userProfiles: UserProfile[]
    commonDeps: CommonDeps
    reportPostId?: UUID
    sheet?: ElementSheet
    featureFlags: Pick<CamelCaseFlags, 'reportsTranslation' | 'reportCommentsTranslation' | 'workflowFeedbackV2'>
    collaboratorUserIds?: number[]
}

export function ElementOptions({
    onClose,
    onFeedbackSubmission,
    onCommentsClick,
    onFeedbackClick,
    onClearElement,
    onQuoteClick,
    isQuoteClickable,
    userProfiles,
    commonDeps,
    reportPostId,
    featureFlags,
    sheet,
    collaboratorUserIds,
}: Props) {
    const commentOption: BottomSheetOptionProps = {
        optionLabel: i18next.t('appcore:element.options.addComment'),
        onClick: onCommentsClick,
        iconName: 'comment',
        testId: 'element-options-comment',
    }
    const feedbackOption: BottomSheetOptionProps = {
        optionLabel: i18next.t('appcore:element.options.addFeedback'),
        onClick: onFeedbackClick,
        iconName: 'lightbulb',
        testId: 'element-options-feedback',
    }

    return (
        <>
            <BaseBottomSheet
                isOpen={sheet?.mode === 'options'}
                onClose={onClose}
                title={i18next.t('appcore:common.options')}
                size={'auto'}
            >
                <BottomSheetOptions options={[commentOption, feedbackOption]}></BottomSheetOptions>
            </BaseBottomSheet>
            <FeedbackSubmission
                elementLabel={sheet?.element && elementDisplayName(sheet.element)}
                open={sheet?.mode === 'feedback'}
                onSubmit={onFeedbackSubmission}
                onClose={onClose}
            />
            <BaseBottomSheet
                isOpen={sheet?.mode === 'comments'}
                onClose={onClose}
                noMargin={true}
                title={i18next.t('appcore:TimelineComment.comments')}
                size={'large'}
                closeButtonTestId={'element-options-close-comments-button'}
            >
                <CommentSection
                    commonDeps={commonDeps}
                    featureFlags={featureFlags}
                    quotedElement={sheet?.element}
                    onClearQuotedElement={onClearElement}
                    onQuoteClick={onQuoteClick}
                    isQuoteClickable={isQuoteClickable}
                    postId={reportPostId}
                    profiles={userProfiles}
                    taggableUserIds={userProfiles.map(({ userId }) => userId)} // TODO(miles): Needs to be limited by confidentiality and collaborators
                    collaboratorUserIds={collaboratorUserIds}
                    showNonCollaboratorNotices={true}
                    focusedComment={sheet?.mode === 'comments' && sheet.focusedComment}
                />
            </BaseBottomSheet>
        </>
    )
}
